import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  TableCell,
  TableRow,
  Chip,
  IconButton,
  TextField,
  InputAdornment,
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, Search as SearchIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import AccountantLayout from '../components/AccountantLayout';
import PaginatedTable from '../components/PaginatedTable';

const TasksPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [tasks, setTasks] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    loadTasks();
  }, [page, rowsPerPage, searchQuery]);

  const loadTasks = async () => {
    setLoading(true);
    // Simulate API call
    setTimeout(() => {
      const mockTasks = [
        { id: 1, title: 'Review Q2 financial statements', client: 'Atlas Construction Co', dueDate: '2026-07-05', priority: 'High', status: 'In Progress' },
        { id: 2, title: 'Prepare VAT return', client: 'Meridian Logistics', dueDate: '2026-07-12', priority: 'High', status: 'Not Started' },
        { id: 3, title: 'Monthly reconciliation', client: 'Orchard Foods Ltd', dueDate: '2026-07-08', priority: 'Medium', status: 'In Progress' },
        { id: 4, title: 'Client meeting preparation', client: 'Seabreeze Properties', dueDate: '2026-07-03', priority: 'Medium', status: 'Not Started' },
        { id: 5, title: 'Annual audit documentation', client: 'Helix Software BV', dueDate: '2026-07-15', priority: 'Low', status: 'Not Started' },
      ];
      setTasks(mockTasks);
      setTotalCount(mockTasks.length);
      setLoading(false);
    }, 500);
  };

  const columns = [
    { id: 'title', label: t('task_title'), minWidth: 250 },
    { id: 'client', label: t('client'), minWidth: 180 },
    { id: 'dueDate', label: t('due_date'), minWidth: 120 },
    { id: 'priority', label: t('priority'), minWidth: 100 },
    { id: 'status', label: t('status'), minWidth: 120 },
    { id: 'actions', label: t('actions'), minWidth: 100, align: 'center' },
  ];

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'High':
        return { bg: '#FFEBEE', color: '#F44336' };
      case 'Medium':
        return { bg: '#FFF3E0', color: '#FF9800' };
      case 'Low':
        return { bg: '#E8F5E9', color: '#4CAF50' };
      default:
        return { bg: '#F5F5F5', color: '#666' };
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Completed':
        return { bg: '#E8F5E9', color: '#4CAF50' };
      case 'In Progress':
        return { bg: '#E3F2FD', color: '#2196F3' };
      case 'Not Started':
        return { bg: '#F5F5F5', color: '#666' };
      default:
        return { bg: '#F5F5F5', color: '#666' };
    }
  };

  const renderRow = (task) => {
    const priorityColor = getPriorityColor(task.priority);
    const statusColor = getStatusColor(task.status);
    return (
      <TableRow hover key={task.id}>
        <TableCell sx={{ fontWeight: 500 }}>{task.title}</TableCell>
        <TableCell>{task.client}</TableCell>
        <TableCell>{task.dueDate}</TableCell>
        <TableCell>
          <Chip
            label={task.priority}
            size="small"
            sx={{
              backgroundColor: priorityColor.bg,
              color: priorityColor.color,
              fontWeight: 500,
            }}
          />
        </TableCell>
        <TableCell>
          <Chip
            label={task.status}
            size="small"
            sx={{
              backgroundColor: statusColor.bg,
              color: statusColor.color,
              fontWeight: 500,
            }}
          />
        </TableCell>
        <TableCell align="center">
          <IconButton size="small" color="primary" onClick={() => navigate(`/accountant-portal/tasks/${task.id}/edit`)}>
            <EditIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" color="error">
            <DeleteIcon fontSize="small" />
          </IconButton>
        </TableCell>
      </TableRow>
    );
  };

  return (
    <AccountantLayout>
      <Box>
        <Box className="accountant-page-header" sx={{ display: 'flex', justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'center' }, flexDirection: { xs: 'column', sm: 'row' }, gap: 2, mb: 3 }}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 0.5 }}>
              {t('accountant_tasks')}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {t('manage_client_tasks')}
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/accountant-portal/tasks/create')}
            sx={{
              textTransform: 'none',
              borderRadius: '8px',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #5568d3 0%, #653a8b 100%)',
              },
            }}
          >
            {t('new_task')}
          </Button>
        </Box>

        <Box sx={{ mb: 3 }}>
          <TextField
            fullWidth
            placeholder={t('search_tasks_placeholder')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{
              backgroundColor: 'white',
              borderRadius: '8px',
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px',
              },
            }}
          />
        </Box>

        <PaginatedTable
          columns={columns}
          data={tasks}
          loading={loading}
          page={page}
          rowsPerPage={rowsPerPage}
          totalCount={totalCount}
          onPageChange={setPage}
          onRowsPerPageChange={setRowsPerPage}
          emptyMessage={t('no_tasks_found')}
          renderRow={renderRow}
        />
      </Box>
    </AccountantLayout>
  );
};

export default TasksPage;
